import React, { useState } from "react";
import { Box, Button, CloseButton, Heading, HStack, Input, InputGroup, Popover, Portal, SimpleGrid, Text, VStack, Separator } from "@chakra-ui/react";
import { LuEraser, LuInfo, LuSquarePen } from "react-icons/lu";
import { saveToCache, showToast } from "../../../utilies/Utility";
import Type from "../../../utilies/Types";
import { useAppStore } from "../../../context/store";

function SettingsMenuHotKeys() {
  const savedHotkeysRef = useAppStore.getState().savedHotkeysRef;
  const defaultHotkeysRef = useAppStore.getState().defaultHotkeysRef;

  const [draftHotkeys, setDraftHotkeys] = useState(() => savedHotkeysRef.current.map((h) => ({ ...h, combination: [...h.combination] })));

  // No sorting: keep exact user-entered order
  const signatureOf = (combo) => (combo && combo.length ? combo.join("+") : "");

  const findConflicts = (items) => {
    const map = new Map();
    for (const it of items) {
      const sig = signatureOf(it.combination);
      if (!sig) continue;
      if (!map.has(sig)) map.set(sig, []);
      map.get(sig).push(it.title || it.id);
    }
    return [...map.entries()].filter(([, ids]) => ids.length > 1);
  };

  const specialKeys = ["shift", "ctrl", "alt", "meta", "space"];
  const allowedPunct = ["/", "-", "=", "[", "]", ";", "'", ",", ".", "`", "\\"];
  const allowedArrows = ["arrowup", "arrowdown", "arrowleft", "arrowright"];

  const normalizeKey = (k) => {
    let key = String(k || "").toLowerCase();
    if (key === "control") key = "ctrl";
    if (key === " ") key = "space";
    return key;
  };

  const isAllowedKey = (key) => /^[a-z]$/.test(key) || specialKeys.includes(key) || allowedPunct.includes(key) || allowedArrows.includes(key);

  const handleKeyDown = (e, index) => {
    e.preventDefault();
    e.stopPropagation();

    const key = normalizeKey(e.key);
    if (!isAllowedKey(key)) return;

    setDraftHotkeys((prev) => {
      const updated = [...prev];
      const combo = [...updated[index].combination];
      if (!combo.includes(key) && combo.length < 4) combo.push(key);
      updated[index] = { ...updated[index], combination: combo };
      return updated;
    });
  };

  const handleClear = (index) => {
    setDraftHotkeys((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], combination: [] };
      return updated;
    });
  };

  const persistHotkeys = (ordered) => {
    const conflicts = findConflicts(ordered);
    if (conflicts.length) {
      const msg = conflicts.map(([sig, ids]) => `${sig.replaceAll("+", " + ")} → ${ids.join(", ")}`).join(" | ");
      showToast(`Duplicate hotkeys: ${msg}`, Type.NOTIFICATION.ERROR);
      return false;
    }

    savedHotkeysRef.current = ordered;
    saveToCache("shortcuts", JSON.stringify(savedHotkeysRef.current));
    return true;
  };

  const handleSave = () => {
    const ordered = draftHotkeys.map((h) => ({ ...h, combination: [...h.combination] }));
    if (persistHotkeys(ordered)) {
      showToast("Hotkeys saved successfully", Type.NOTIFICATION.SUCCESS);
    }
  };

  const handleReset = () => {
    const defaults = defaultHotkeysRef.current.map((h) => ({ ...h, combination: [...h.combination] }));

    setDraftHotkeys(defaults);

    if (persistHotkeys(defaults)) {
      showToast("Hotkeys reset to defaults", Type.NOTIFICATION.SUCCESS);
    }
  };

  const groupOrder = ["general", "geometry", "editing", "navigation", "topology", "utility", "switch", "tools"];
  const grouped = groupOrder.map((group) => ({
    name: group,
    items: draftHotkeys.filter((hk) => hk.group === group),
  }));

  return (
    <Box>
      {grouped.map(({ name, items }) =>
        items.length > 0 ? (
          <Box key={name} mb={4}>
            <Heading size="lg" mb={0} textTransform="capitalize">
              {name}
            </Heading>

            <SimpleGrid columns={4} gap={3} width="full" mb={2} px={2}>
              {items.map((hk) => {
                // No sorting in display
                const value = (hk.combination || []).join(" + ");
                const endElement = value.length > 0 ? <CloseButton size="xs" onClick={() => handleClear(draftHotkeys.indexOf(hk))} me="-2" /> : undefined;

                return (
                  <VStack width="full" key={hk.id} gap={0}>
                    <Heading width="full" textAlign="left">
                      <Popover.Root>
                        <Popover.Trigger asChild>
                          <Button size="sm" variant="unstyled" bg="transparent" border="none" p={0} m={0} justifyContent={"left"}>
                            <LuInfo />
                            <Text fontSize="14px">{hk.title}</Text>
                          </Button>
                        </Popover.Trigger>
                        <Portal>
                          <Popover.Positioner shadow={"lg"}>
                            <Popover.Content width={"fit-content"}>
                              <Popover.Arrow />
                              <Popover.Body py={3} px={5}>
                                {hk.desc}
                              </Popover.Body>
                            </Popover.Content>
                          </Popover.Positioner>
                        </Portal>
                      </Popover.Root>
                    </Heading>

                    <InputGroup endElement={endElement}>
                      <Input placeholder="Press hotkey..." value={value} readOnly onKeyDown={(e) => handleKeyDown(e, draftHotkeys.indexOf(hk))} onPaste={(e) => e.preventDefault()} />
                    </InputGroup>
                  </VStack>
                );
              })}
            </SimpleGrid>

            <Separator my={2} />
          </Box>
        ) : null,
      )}

      <HStack mt={3}>
        <Button onClick={handleSave}>
          Save Changes <LuSquarePen />
        </Button>
        <Button variant="outline" onClick={handleReset}>
          Restore Defaults <LuEraser />
        </Button>
      </HStack>
    </Box>
  );
}

export default SettingsMenuHotKeys;
